/**
 * Browser noise that is not worth recording, dropped at the sink.
 *
 * "ResizeObserver loop completed with undelivered notifications." is the whole
 * list today. It is a browser housekeeping notice, not a fault: Chrome raises it
 * when an observer callback resizes something and the next frame has to run the
 * callbacks again. Nothing is broken, nothing is lost, and there is no code
 * change that makes it stop - which is why the usual advice is to ignore it.
 *
 * Ignoring it is exactly what was not happening. In the week to 2026-09-22 it was
 * 99 of the 1,672 events in PostHog - about six per cent of all telemetry, from
 * two browsers - and 8 of the 9 real errors in the same period were Chart.js
 * canvas reuse, sitting underneath it. Error data that is 99 parts noise is error
 * data nobody reads, and it is billed by the event at both sinks.
 *
 * Dropped at the sink rather than wrapped around the code that provokes it: the
 * observers are inside Vuetify's own components, so there is no application call
 * site to guard, and a window.onerror swallow would hide the message from the
 * developer console as well. Here it is only the recording that stops.
 *
 * Both sinks import this, so a message added to the list is dropped from PostHog
 * and from Grafana Faro at once and the two cannot drift apart.
 */
const IGNORED_MESSAGES = [
  /ResizeObserver loop completed with undelivered notifications/i,
  /ResizeObserver loop limit exceeded/i,
];

const matches = (value) => {
  if (!value) {
    return false;
  }
  const text = `${value}`;
  return IGNORED_MESSAGES.some((pattern) => pattern.test(text));
};

/**
 * True when this text is browser noise we do not record.
 * Accepts a string, or an array of them as PostHog's $exception_values arrives.
 */
export const isIgnoredMessage = (value) => {
  if (Array.isArray(value)) {
    return value.some(matches);
  }
  return matches(value);
};

/**
 * PostHog `before_send`: return the event to send it, or null to drop it.
 *
 * An exception arrives as $exception_list (an array of {type, value}) on current
 * SDKs and as $exception_values (an array of strings) on older ones. Read both -
 * the SDK decides which it sends, not us.
 */
export const dropNoisyEvent = (event) => {
  try {
    if (!event || event.event !== '$exception') {
      return event;
    }
    const properties = event.properties || {};
    if (isIgnoredMessage(properties.$exception_values)) {
      return null;
    }
    if (isIgnoredMessage(properties.$exception_message)) {
      return null;
    }
    const list = properties.$exception_list;
    if (Array.isArray(list) && list.some((entry) => isIgnoredMessage(entry && entry.value))) {
      return null;
    }
    return event;
  } catch (e) {
    // A filter that throws must not be the reason an event goes missing.
    return event;
  }
};

/**
 * Grafana Faro: true when this item is noise the sink should drop.
 * An exception item carries the message in payload.value.
 */
export const isNoisyFaroItem = (item) => {
  try {
    const payload = item && item.payload;
    if (!payload) {
      return false;
    }
    return isIgnoredMessage(payload.value) || isIgnoredMessage(payload.message);
  } catch (e) {
    return false;
  }
};

export default isIgnoredMessage;
